import React, { useEffect, useState } from 'react'
import { motion, Variants } from 'framer-motion'

import overviewIcon from '../../../assets/images/medilearn/overviewbg.svg'
import plus from '../../../assets/images/medilearn/plus.svg'

import img1 from '../../../assets/images/medilearn/overview1.webp'
import img2 from '../../../assets/images/medilearn/overview2.webp'
import img3 from '../../../assets/images/medilearn/overview3.webp'

// ─── dots ────────────────────────────────────────────────────────────────────
const dots = [
  { top: '8%', left: '6%', size: 5, opacity: 0.7 },
  { top: '18%', right: '12%', size: 5, opacity: 0.7 },
  { top: '28%', left: '22%', size: 5, opacity: 0.7 },
  { top: '40%', right: '8%', size: 5, opacity: 0.7 },
  { top: '56%', left: '10%', size: 5, opacity: 0.7 },
  { top: '72%', right: '22%', size: 5, opacity: 0.7 },
  { top: '88%', left: '28%', size: 5, opacity: 0.7 },

  { top: '12%', left: '38%', size: 10, opacity: 0.55 },
  { top: '24%', right: '28%', size: 10, opacity: 0.55 },
  { top: '34%', left: '8%', size: 10, opacity: 0.55 },
  { top: '48%', right: '35%', size: 10, opacity: 0.55 },
  { top: '60%', left: '30%', size: 10, opacity: 0.55 },
  { top: '76%', right: '12%', size: 10, opacity: 0.55 },
  { top: '90%', left: '42%', size: 10, opacity: 0.55 },

  { top: '10%', right: '40%', size: 16, opacity: 0.4 },
  { top: '22%', left: '15%', size: 16, opacity: 0.4 },
  { top: '36%', right: '58%', size: 16, opacity: 0.4 },
  { top: '50%', left: '42%', size: 16, opacity: 0.4 },
  { top: '66%', right: '8%', size: 16, opacity: 0.4 },
  { top: '80%', left: '8%', size: 16, opacity: 0.4 },
  { top: '92%', right: '32%', size: 16, opacity: 0.4 },
]

// ─── scale animation ────────────────────────────────────────────────────────
const scaleVariant: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.7,
  },

  visible: {
    opacity: 1,
    scale: 1,

    transition: {
      duration: 1,
      ease: [0.22, 1, 0.36, 1],
    },
  },
}

// ─── image slots ────────────────────────────────────────────────────────────
const SLOT_CLASS = [
  'w-[20%] md:w-[15%] xl:w-[14%] 2xl:w-[30%]', // left
  'w-[45%] md:w-[30%] xl:w-[30%] 2xl:w-[30%]', // center
  'w-[20%] md:w-[15%] xl:w-[14%] 2xl:w-[30%]', // right
]

const SLOT_Z = [10, 20, 10]

const SLOT_SCALE = [0.88, 1.08, 0.88]

const IMAGES = [img1, img2, img3]

// ─── transition fix ─────────────────────────────────────────────────────────
const SWAP_TRANSITION = {
  type: 'spring' as const,
  stiffness: 32,
  damping: 24,
  mass: 1.8,
}

const HOLD_MS = 5000

// ─── component ──────────────────────────────────────────────────────────────
const OverviewMedilearn = () => {
  const [order, setOrder] = useState<number[]>([0, 1, 2])

  useEffect(() => {
    const id = setInterval(() => {
      // right → left swapping
      setOrder(prev => prev.map(slot => (slot + 1) % 3))
    }, HOLD_MS)

    return () => clearInterval(id)
  }, [])

  return (
    <section className="relative overflow-hidden py-10 md:py-16 xl:py-24">

      {/* WHITE DOTS */}
      <div className="pointer-events-none absolute inset-0 z-0">

        {dots.map((dot, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              top: dot.top,
              left: dot.left,
              right: dot.right,

              width:
                dot.size === 5
                  ? '3px'
                  : dot.size === 10
                    ? '6px'
                    : '10px',

              height:
                dot.size === 5
                  ? '3px'
                  : dot.size === 10
                    ? '6px'
                    : '10px',

              opacity: dot.opacity,
            }}
          />
        ))}

      </div>

      {/* LEFT GLOW */}
      <div
      
        className="pointer-events-none absolute left-0 top-[78%] z-0 h-[80px] w-[150px] -translate-y-1/2 md:top-[65%] md:h-[150px] md:w-[300px] lg:h-[250px] lg:w-[500px] xl:top-[60%]"
        style={{
          background:
            'radial-gradient(82.96% 47.27% at 0.26% 49.19%, #D4FF76 30%, rgba(212,255,118,0) 100%)',
        }}
      />

      {/* RIGHT GLOW */}
      <div
        className="pointer-events-none absolute right-0 top-[78%] z-0 h-[80px] w-[150px] md:top-[65%] md:h-[150px] md:w-[300px] lg:h-[250px] lg:w-[500px] xl:top-[60%]"
        style={{
          background:
            'radial-gradient(82.96% 47.27% at 0.26% 49.19%, #D4FF76 30%, rgba(212,255,118,0) 100%)',
          transform: 'translate(0,-50%) scaleX(-1)',
        }}
      />

      {/* MAIN */}
      <div className="relative z-10 mx-auto max-w-[1900px]">

        {/* HEADER */}
        <motion.div
          variants={scaleVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="flex items-center justify-center gap-6"
        >

          <div className="h-3 w-3 rounded-full bg-white" />

          <h2 className="font-gilroy-extrabold text-2xl text-black md:text-4xl xl:text-5xl 2xl:text-6xl">
            OVERVIEW
          </h2>

          <div className="h-3 w-3 rounded-full bg-white" />

        </motion.div>

        {/* DESCRIPTION */}
        <motion.div
          variants={scaleVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          className="relative"
        >

          <p className="mx-auto mt-4 px-5 text-center font-gilroy-medium text-lg text-white/60 md:max-w-[85%] lg:mt-8 lg:text-2xl xl:max-w-[75%] xl:text-3xl 2xl:mb-[-6%] 2xl:text-4xl">
            Medilearn is an AI-driven medical learning and assistance app
            designed to simplify how users understand symptoms, explore
            medical knowledge, &amp; get guided health insights.
          </p>

          {/* ROTATING PLUS */}
          <motion.img
            src={plus}
            alt=""
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="
              pointer-events-none
              absolute
              left-[5%]
              top-[85%]
              w-[40px]
              md:left-[10%]
              md:w-[50px]
              xl:left-[15%]
              xl:w-[60px]
              2xl:left-[4%]
              2xl:w-[80px]
            "
          />

        </motion.div>

        {/* IMAGES */}
        <div className="relative mt-8 overflow-hidden 2xl:mt-20">

          <div className="relative flex items-center justify-center">

            {/* BG */}
            <img
              src={overviewIcon}
              alt=""
              className="
                pointer-events-none
                absolute
                left-1/2
                top-1/2
                z-0
                w-[120%]
                -translate-x-1/2
                -translate-y-1/2
                object-contain
                md:w-[110%]
                xl:w-[100%]
              "
            />

{/* SWAPPING IMAGES */}
<div className="relative z-10 h-[250px] md:h-[340px] lg:h-[400px] xl:h-[500px] 2xl:h-[720px] flex w-full items-center justify-center gap-2 md:gap-8">

  {IMAGES.map((src, imgIdx) => {
    const slot = order[imgIdx]

    return (
      <motion.div
        key={imgIdx}
        layout
        transition={SWAP_TRANSITION}
        style={{
          order: slot,
          zIndex: SLOT_Z[slot],
        }}
        className="
          flex
          justify-center
          w-[25%]
          md:w-[20%]
          lg:w-[18%]
          xl:w-[16%]
          2xl:w-[18%]
          shrink-0
        "
      >
        <motion.img
          src={src}
          alt=""
          animate={{
            scale: slot === 1 ? 1.5 : 1,
          }}
          transition={SWAP_TRANSITION}
          className="w-full object-contain"
        />
      </motion.div>
    )
  })}

</div>
          </div>

        </div>

      </div>

    </section>
  )
}

export default OverviewMedilearn